import React, { useMemo, useRef } from 'react';

import { SettingsMenuCommunities } from '@lumapps/communities/components/SettingsMenuCommunities';
import { useCommunity } from '@lumapps/communities/hooks/useCommunity';
import {
    SettingsMenuCustomContentType,
    SettingsMenuCustomContentTypeProps,
} from '@lumapps/content-types/components/SettingsMenuCustomContentType';
import { ContentEditSettingsMenu } from '@lumapps/contents/components/ContentEditSettingsMenu';
import { useContent } from '@lumapps/contents/hooks/useContent';
import { PLACEMENT, Targets } from '@lumapps/customizations/api';
import { CustomizationComponent } from '@lumapps/customizations/api/components/CustomizationComponent';
import { useDataAttributes } from '@lumapps/data-attributes';
import { Menu } from '@lumapps/lumx-menu/components/Menu';
import { mdiSettings } from '@lumapps/lumx/icons';
import { Emphasis, IconButton, Offset, Placement, Size, Theme } from '@lumapps/lumx/react';
import { calculateThemeColor } from '@lumapps/lumx/utils/calculateThemeColor';
import { useMetadataAdminRights } from '@lumapps/metadata/hooks/useMetadataAdminRights';
import {
    SettingsMenuSocialAdvocacy,
    SettingsMenuSocialAdvocacyProps,
} from '@lumapps/sa-navigation/components/SettingsMenuSocialAdvocacy';
import { SettingsMenuSpaces } from '@lumapps/spaces/components/SettingsMenuSpaces';
import { useSpace } from '@lumapps/spaces/hooks/useSpace';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import {
    SettingsMenuContentStrategy,
    SettingsMenuContentStrategyProps,
} from './SettingsMenuContentStrategy/SettingsMenuContentStrategy';
import { SettingsMenuModules, SettingsMenuModulesProps } from './SettingsMenuModules/SettingsMenuModules';
import { SettingsMenuSite, SettingsMenuSiteProps } from './SettingsMenuSite/SettingsMenuSite';

import './index.scss';

export interface SettingsMenuProps
    extends SettingsMenuModulesProps,
        SettingsMenuCustomContentTypeProps,
        SettingsMenuSocialAdvocacyProps,
        SettingsMenuContentStrategyProps,
        SettingsMenuSiteProps {
    placement?: Placement;
    className?: string;
    offset?: Offset;
    isVisible: boolean;
    SAEnabled: boolean;
    isNewCommunityNavigationEnabled: boolean;
    canEditSpace: boolean;
    onCommunityConfigurationEdit(): void;
    onCommunityNavigationEdit(): void;
    onSpaceConfigurationEdit(): void;
    onSpaceNavigationEdit(): void;
    theme?: string;
}

const NAMESPACE = 'settings-menu';
export const SettingsMenu: React.FC<SettingsMenuProps> = ({
    placement = Placement.BOTTOM_END,
    className,
    offset = {},
    isVisible = true,

    // Community Section
    onCommunityConfigurationEdit,
    onCommunityNavigationEdit,
    isNewCommunityNavigationEnabled,

    // Space Section
    canEditSpace,
    onSpaceConfigurationEdit,
    onSpaceNavigationEdit,

    // Custom Content Types Section
    fetchCustomContentTypes,
    isLoading = false,
    customContentTypes,
    parentCustomContentTypesIds,
    isSuperAdmin = false,
    allowedCustomContentTypes = {},
    isAllowedToAccessCCT,

    // Module Section
    canAccessModules,
    canAccessCommunities,
    canEditDirectories,
    directories,
    canEditNewsletter,
    canAccessUserDirectories,

    // SA Section
    SAEnabled,
    userCanManagePlatformPrograms,
    userCanManagePlatformSocialNetworks,

    // Content Strategy Section
    canAccessBroadcast,
    canAccessJourneys,
    canAccessSegment,
    canAccessContentStream,

    // Site section
    canAccessSiteAnalytics,
    hasMainNavAdminAccess,
    canEditInstance,
    canAccessMediaLibrary,
    theme = Theme.light,
}) => {
    const iconRef = useRef(null);
    const { translateKey } = useTranslate();

    const { get } = useDataAttributes(NAMESPACE);

    const { canCurrentUserEditContent, currentContentId, currentContentInstanceSlug } = useContent();
    const { canEditCommunity, currentCommunityId, view, slug, canViewCommunityAnalytics, isCommunityPage } =
        useCommunity();
    const { isSpace } = useSpace();

    const { isAllowed: hasMetadataAdminRights } = useMetadataAdminRights();

    // If the user can access nothing, do not show the icon
    const canAccessMenu = useMemo(
        () =>
            isSuperAdmin ||
            canCurrentUserEditContent ||
            canEditCommunity ||
            canEditSpace ||
            canViewCommunityAnalytics ||
            isAllowedToAccessCCT ||
            canAccessCommunities ||
            canAccessModules ||
            canEditDirectories ||
            canEditNewsletter ||
            userCanManagePlatformPrograms ||
            userCanManagePlatformSocialNetworks ||
            canAccessSiteAnalytics ||
            hasMainNavAdminAccess ||
            canEditInstance ||
            canAccessBroadcast ||
            canAccessJourneys ||
            canAccessSegment ||
            hasMetadataAdminRights ||
            canAccessMediaLibrary,

        [
            isSuperAdmin,
            canCurrentUserEditContent,
            canEditCommunity,
            canEditSpace,
            canViewCommunityAnalytics,
            isAllowedToAccessCCT,
            canAccessCommunities,
            canAccessModules,
            canEditDirectories,
            canEditNewsletter,
            userCanManagePlatformPrograms,
            userCanManagePlatformSocialNetworks,
            canAccessSiteAnalytics,
            hasMainNavAdminAccess,
            canEditInstance,
            canAccessBroadcast,
            canAccessJourneys,
            canAccessSegment,
            hasMetadataAdminRights,
            canAccessMediaLibrary,
        ],
    );

    if (!canAccessMenu || !isVisible) {
        return null;
    }

    return (
        <Menu.Trigger popoverProps={{ placement, offset, width: 'xxl' }}>
            <Menu.TriggerButton
                as={IconButton}
                icon={mdiSettings}
                size={Size.m}
                emphasis={Emphasis.low}
                className={className}
                ref={iconRef}
                color={calculateThemeColor(theme)}
                label={translateKey(GLOBAL.SETTINGS)}
                {...get({
                    element: 'button',
                    permissions: {
                        isAllowedToAccessCCT,
                        canCurrentUserEditContent,
                        canEditCommunity,
                        canViewCommunityAnalytics,
                        canAccessCommunities,
                        canEditDirectories,
                        canEditNewsletter,
                        userCanManagePlatformPrograms,
                        userCanManagePlatformSocialNetworks,
                        canAccessSiteAnalytics,
                        hasMainNavAdminAccess,
                        canEditInstance,
                        hasMetadataAdminRights,
                        canAccessMediaLibrary,
                    },
                })}
            />

            <Menu className={NAMESPACE}>
                <CustomizationComponent target={Targets.SETTINGS} placement={PLACEMENT.ABOVE} />

                {!isSpace && (
                    <ContentEditSettingsMenu
                        canEditContent={canCurrentUserEditContent}
                        contentId={currentContentId}
                        contentInstanceSlug={currentContentInstanceSlug}
                        scope={NAMESPACE}
                    />
                )}
                <SettingsMenuSpaces
                    key="space"
                    scope={NAMESPACE}
                    onSpaceEdit={onSpaceConfigurationEdit}
                    onSpaceNavigationEdit={onSpaceNavigationEdit}
                />
                {isCommunityPage && (
                    <SettingsMenuCommunities
                        canEditCommunity={canEditCommunity}
                        canViewCommunityAnalytics={canViewCommunityAnalytics}
                        communityId={currentCommunityId}
                        communityTemplate={view}
                        communitySlug={slug}
                        scope={NAMESPACE}
                        onCommunityEdit={onCommunityConfigurationEdit}
                        onCommunityNavigationEdit={onCommunityNavigationEdit}
                        isNewCommunityNavigationEnabled={isNewCommunityNavigationEnabled}
                    />
                )}
                <SettingsMenuCustomContentType
                    fetchCustomContentTypes={fetchCustomContentTypes}
                    isLoading={isLoading}
                    customContentTypes={customContentTypes}
                    parentCustomContentTypesIds={parentCustomContentTypesIds}
                    isSuperAdmin={isSuperAdmin}
                    allowedCustomContentTypes={allowedCustomContentTypes}
                    isAllowedToAccessCCT={isAllowedToAccessCCT}
                    scope={NAMESPACE}
                />
                <SettingsMenuModules
                    canAccessCommunities={canAccessCommunities}
                    canEditDirectories={canEditDirectories}
                    directories={directories}
                    canEditNewsletter={canEditNewsletter}
                    canAccessModules={canAccessModules}
                    canAccessUserDirectories={canAccessUserDirectories}
                    scope={NAMESPACE}
                />
                <SettingsMenuContentStrategy
                    canAccessBroadcast={canAccessBroadcast}
                    canAccessJourneys={canAccessJourneys}
                    canAccessSegment={canAccessSegment}
                    canAccessContentStream={canAccessContentStream}
                    scope={NAMESPACE}
                />
                {SAEnabled && (
                    <SettingsMenuSocialAdvocacy
                        userCanManagePlatformPrograms={userCanManagePlatformPrograms}
                        userCanManagePlatformSocialNetworks={userCanManagePlatformSocialNetworks}
                        scope={NAMESPACE}
                    />
                )}
                <SettingsMenuSite
                    canAccessSiteAnalytics={canAccessSiteAnalytics}
                    hasMainNavAdminAccess={hasMainNavAdminAccess}
                    canEditInstance={canEditInstance}
                    hasMetadataAdminRights={hasMetadataAdminRights}
                    canAccessMediaLibrary={canAccessMediaLibrary}
                    scope={NAMESPACE}
                />

                <CustomizationComponent target={Targets.SETTINGS} placement={PLACEMENT.UNDER} />
            </Menu>
        </Menu.Trigger>
    );
};

SettingsMenu.displayName = 'SettingsMenu';
